<template>
  <SDialog :open="true" @update:open="onOpenChange">
    <SDialogContent
      data-cy="team-member-dialog"
      class="!w-[560px] !max-w-[95vw]"
      @escape-key-down.prevent
      @pointer-down-outside.prevent
      @focus-outside.prevent
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle>
          {{ isEdit ? $t("team.edit-user") : $t("team.create-user-title") }}
        </SDialogTitle>
        <SSeparator class="mb-0" />
      </SDialogHeader>

      <div class="flex flex-col">
        <!-- Role -->
        <form.Field name="roleType" v-slot="{ field }">
          <SField
            data-cy="team-field-role"
            :data-invalid="showError(field) || undefined"
            class="mb-4"
          >
            <SFieldLabel>
              {{ $t("team.role") }}
              <span class="text-destructive" aria-hidden="true">*</span>
            </SFieldLabel>
            <SSelect
              :model-value="field.state.value || undefined"
              :disabled="roleSelectDisabled"
              @update:model-value="(v) => field.handleChange(Number(v))"
            >
              <SSelectTrigger class="w-full" data-cy="team-field-role-trigger">
                <SSelectValue :placeholder="$t('button.select')" />
              </SSelectTrigger>
              <SSelectContent>
                <SSelectItem
                  v-for="option in roleOptions"
                  :key="option.value"
                  :value="option.value"
                  :data-cy="`team-role-option-${option.value}`"
                >
                  {{ option.label }}
                </SSelectItem>
              </SSelectContent>
            </SSelect>
            <p
              v-if="roleSelectDisabled && roleLockHint"
              class="text-xs text-muted-foreground"
              data-cy="team-role-lock-hint"
            >
              {{ roleLockHint }}
            </p>
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>

        <!-- Email (read-only on edit; the invitation is bound to it) -->
        <form.Field name="email" v-slot="{ field }">
          <SField
            data-cy="team-field-email"
            :data-invalid="showError(field) || undefined"
            class="mb-4"
          >
            <SFieldLabel :for="field.name">
              {{ $t("team.email") }}
              <span class="text-destructive" aria-hidden="true">*</span>
            </SFieldLabel>
            <SInput
              :id="field.name"
              type="email"
              :model-value="field.state.value"
              :disabled="isEdit"
              :aria-invalid="showError(field) || undefined"
              @update:model-value="(v) => field.handleChange(String(v).trim())"
              @blur="field.handleBlur"
            />
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>

        <!-- Social security number -->
        <form.Field name="socialSecurityNumber" v-slot="{ field }">
          <SField
            data-cy="team-field-ssn"
            :data-invalid="showError(field) || undefined"
            class="mb-4"
          >
            <SFieldLabel :for="field.name">
              {{ $t("team.social-security-number") }}
            </SFieldLabel>
            <SInput
              :id="field.name"
              :model-value="field.state.value"
              :aria-invalid="showError(field) || undefined"
              @update:model-value="field.handleChange"
              @blur="field.handleBlur"
            />
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>
      </div>

      <SDialogFooter class="gap-3">
        <SButton
          type="button"
          variant="outline"
          class="min-w-[120px]"
          data-cy="team-member-cancel-button"
          @click="$emit('close')"
        >
          {{ $t("button.cancel") }}
        </SButton>
        <SButton
          type="button"
          class="min-w-[120px]"
          :loading="isSaving"
          data-cy="team-member-save-button"
          @click="onSave"
        >
          {{ $t("button.save") }}
        </SButton>
      </SDialogFooter>
    </SDialogContent>
  </SDialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SField,
  SFieldError,
  SFieldLabel,
  SInput,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
  SSeparator,
} from "@smeetz/ui-core";
import { useSmeetzForm } from "@smeetz/services-validation";

import { patchTeamMember, postTeamMember } from "@/api/team/team";
import { FALLBACK_ROLE_LABELS, POS_PIN_ROLE_TYPES } from "@/constants/team";
import { UserRole } from "@/interfaces/store/IUserState";
import { ITeamMember } from "@/interfaces/responses/team";
import AppModule from "@/store/modules/app";
import UserModule from "@/store/modules/user";
import NotificationService from "@/services/notification.service";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import { processRoleChangeEventV2 } from "@/utils/helpers/compliance-v2";
import { isFrenchCompliant } from "@/utils/vue-helpers/compliance";
import { teamMemberSchema, TeamMemberFormValues } from "./team.form";

// ---------- Props & Emits ----------
const props = defineProps<{
  /** Member being edited; null when creating. */
  member: ITeamMember | null;
  /** Current team, used for guardrails and the duplicate-email check. */
  members: ITeamMember[];
  /** Role id → label map from GET /group/roles (may be empty offline). */
  roleLabels: Record<number, string>;
  groupId: number;
}>();

const emit = defineEmits<{
  (event: "close"): void;
  (event: "saved"): void;
}>();

const { t } = useI18n({ useScope: "global" });

// ---------- State ----------
const isEdit = computed(() => !!props.member);
const isSaving = ref(false);

const existingEmails = props.members
  .filter((member) => member.roleId !== props.member?.roleId)
  .map((member) => member.email.toLowerCase())
  .filter(Boolean);

const { form, showError, firstErrorText } = useSmeetzForm<TeamMemberFormValues>(
  {
    defaultValues: {
      roleType: props.member?.roleType ?? 0,
      email: props.member?.email ?? "",
      socialSecurityNumber: props.member?.socialSecurityNumber ?? "",
    },
    schema: teamMemberSchema({ isEdit: isEdit.value, existingEmails }),
    validatorTiming: "onSubmit",
  }
);

// ---------- Computed ----------
const ownersCount = computed(
  () =>
    props.members.filter((member) => member.roleType === UserRole.ROLE_OWNER)
      .length
);

const isSelfMember = computed(
  () =>
    !!props.member &&
    props.member.userId !== null &&
    props.member.userId === UserModule.getUserId
);

const isLastOwnerMember = computed(
  () =>
    !!props.member &&
    props.member.roleType === UserRole.ROLE_OWNER &&
    ownersCount.value === 1
);

// Nobody may change their own role, and the last owner cannot be demoted.
const roleSelectDisabled = computed(
  () => isEdit.value && (isSelfMember.value || isLastOwnerMember.value)
);

const roleLockHint = computed(() => {
  if (!isEdit.value) {
    return "";
  }
  if (isSelfMember.value) {
    return t("team.cannot-change-own-role");
  }
  if (isLastOwnerMember.value) {
    return t("team.last-owner-warning");
  }

  return "";
});

// One flat list in catalogue order, mirroring the back-office select.
// PIN roles (POS Seller / POS Seller Manager) are created through the
// back-office PIN flow, which the POS does not offer for now.
const roleOptions = computed(() => {
  const labels = Object.keys(props.roleLabels).length
    ? props.roleLabels
    : FALLBACK_ROLE_LABELS;

  return Object.entries(labels)
    .map(([value, label]) => ({ value: Number(value), label: String(label) }))
    .filter(
      (option) =>
        option.value !== UserRole.ROLE_NO_ROLE &&
        !POS_PIN_ROLE_TYPES.includes(option.value)
    )
    .sort((a, b) => a.value - b.value);
});

// ---------- Helpers ----------
/**
 * Label for a role id, preferring the fetched catalogue over the local
 * fallback so the journal entry carries the same wording as the back office.
 *
 * @param {number} roleType - Numeric role id.
 * @returns {string} Human-readable role name.
 */
function roleName(roleType: number): string {
  return (
    props.roleLabels[roleType] ||
    FALLBACK_ROLE_LABELS[roleType] ||
    String(roleType)
  );
}

// ---------- Handlers ----------
function onOpenChange(open: boolean): void {
  if (!open) {
    emit("close");
  }
}

async function onSave(): Promise<void> {
  await form.handleSubmit();

  if (!form.state.isValid) {
    NotificationService.showCustomMessage(t("error.missing-fields"), "warning");

    return;
  }

  const values = form.state.values as TeamMemberFormValues;

  isSaving.value = true;

  try {
    if (isEdit.value && props.member) {
      // Same payload as the back-office edit flow: email, role and social
      // security number travel together on every PATCH.
      const nextRoleType = roleSelectDisabled.value
        ? props.member.roleType
        : values.roleType;
      const roleChanged = nextRoleType !== props.member.roleType;

      await patchTeamMember({
        groupId: props.groupId,
        roleId: props.member.roleId,
        email: props.member.email,
        roleType: nextRoleType,
        socialSecurityNumber: values.socialSecurityNumber.trim(),
      });
      NotificationService.showCustomSuccess(t("team.member-updated"));

      // Operator rights are part of the fiscal record: journal the change in
      // the French JET (audit 130). The role is already saved, so a failed
      // entry is reported as a journalling failure, never as a failed save.
      if (roleChanged && isFrenchCompliant.value) {
        const journalled = await processRoleChangeEventV2({
          memberName: props.member.fullName || props.member.email,
          previousRoleName: roleName(props.member.roleType),
          newRoleName: roleName(nextRoleType),
        });

        if (!journalled) {
          NotificationService.showCustomMessage(
            t("team.role-change-not-journalled"),
            "warning"
          );
        }
      }
    } else {
      await postTeamMember({
        groupId: props.groupId,
        email: values.email.trim(),
        role: values.roleType,
        invitingUserLocale: AppModule.language,
        socialSecurityNumber: values.socialSecurityNumber.trim(),
      });
      NotificationService.showCustomSuccess(t("team.member-invited"));
    }

    emit("saved");
  } catch (error) {
    NotificationService.showCustomError(
      handleErrorAndPrepareMessage(error).errorMessage
    );
  } finally {
    isSaving.value = false;
  }
}
</script>
